/**
 * De un fallo cualquiera a **una frase que una persona puede leer**.
 *
 * ── El fallo que este archivo existe para que no vuelva a pasar ────────────
 * El 2026-09-25 un comensal sentado en una mesa vio esto en su pantalla, dentro
 * del formulario de alta:
 *
 *     Faltan VITE_SUPABASE_URL y/o VITE_SUPABASE_LLAVE_PUBLICABLE. Es la llave
 *     publicable (sb_publishable_…), NUNCA la secreta: ésta acaba dentro del
 *     JavaScript que descarga cualquiera.
 *
 * Es un mensaje **para quien despliega**, y estaba en la cara de un cliente de
 * nuestro cliente. Llegó por una línea que parece prudente y no lo es:
 *
 *     catch (err) { setFallo(err instanceof Error ? err.message : '…') }
 *
 * `instanceof Error` no acota nada: **todo** lo que se lanza en JavaScript es un
 * `Error`. Esa condición dice «si trae mensaje, enséñalo», que es exactamente lo
 * contrario de lo que hace falta.
 *
 * ── La regla, y es una inversión ───────────────────────────────────────────
 * Un mensaje sólo llega a la pantalla si **alguien lo escribió para la
 * pantalla**. Todo lo demás se sustituye, se registra y no se enseña. Es la
 * misma regla que `Estandares` ya exige del otro lado —«nunca se filtra un error
 * de base de datos al cliente: revela esquema y a veces datos»— aplicada donde
 * faltaba.
 *
 * De ahí que sólo `ErrorDeApi` pase: su mensaje viene del campo `mensaje` que la
 * API redacta en español para leerse. Cualquier otra cosa —un fallo de
 * configuración, un `TypeError` nuestro, una librería que se queja— es interna
 * por definición, porque nadie la escribió pensando en quien la va a leer.
 */
import { ErrorDeApi } from './cliente.js';
/**
 * Un fallo cuyo mensaje **está escrito para quien lo va a leer**.
 *
 * Existe porque el tipo tiene que decirlo. En `supabase.ts` convivían dos
 * `new Error` indistinguibles: uno decía «Ese código no funcionó. Revísalo o
 * pide uno nuevo» —para el comensal— y el otro nombraba variables de entorno y
 * explicaba cuál llave no publicar —para quien despliega—. Con los dos como
 * `Error` pelado, cualquier filtro acierta con uno y falla con el otro.
 *
 * Lanzar esto es una afirmación: **«esta frase la escribí para una pantalla»**.
 * Lo que no la lleve se considera interno, que es la única forma de que un
 * mensaje nuevo nazca oculto en vez de nacer publicado.
 */
export class ErrorParaLaPantalla extends Error {
    constructor(mensaje) {
        super(mensaje);
        this.name = 'ErrorParaLaPantalla';
    }
}
/**
 * Lo que se enseña cuando el navegador no pudo ni salir a la red.
 *
 * `fetch` rechaza con `TypeError` cuando no hay conexión, cuando el servidor no
 * contesta o cuando CORS lo para. Es el único fallo que el comensal **puede
 * arreglar**, así que merece su propia frase en vez del respaldo genérico.
 */
export const SIN_CONEXION = 'No se pudo conectar. Revisa tu internet e inténtalo otra vez.';
/**
 * Qué hacer ahora, **puesto aquí y no en cada respaldo**.
 *
 * Un aviso que dice qué se rompió y no qué hacer deja a quien lo lee sin saber
 * si esperar, si volver a tocar el botón o si ya no hay nada que hacer. Lo pidió
 * Isaac el 2026-09-25 mirando «No se pudo completar tu registro.» a secas.
 *
 * Va en la función y no en los seis textos de las pantallas por lo mismo que el
 * `console.error`: **lo que hay que acordarse de escribir acaba faltando**. De
 * los seis respaldos que existían, cuatro no lo decían. Así el que se escriba
 * mañana lo lleva sin que nadie se acuerde.
 *
 * Y sólo alcanza al respaldo, que es donde siempre es verdad: el respaldo es lo
 * que se enseña cuando **no sabemos qué pasó**, y ahí volver a intentarlo es
 * exactamente el consejo correcto. Los otros dos caminos ya traen el suyo —la
 * API redacta el suyo, y `SIN_CONEXION` dice qué revisar—, así que ninguno lo
 * recibe.
 */
export const MAS_TARDE = 'Inténtalo más tarde.';
/**
 * La frase para la pantalla.
 *
 * `respaldo` es lo que se dice cuando el fallo no es de la API: escríbelo
 * pensando en **qué estaba intentando hacer** quien lo va a leer —«No se pudo
 * mandar el código»— y no en qué se rompió por dentro. **Sólo esa mitad**: el
 * «inténtalo más tarde» lo pone esta función. Ver `MAS_TARDE`.
 *
 * > [!info] El error de verdad no se pierde: se registra
 * > Va a la consola con su contexto. Lo que cambia es **quién lo lee**: quien
 * > abre las herramientas de desarrollo, no quien está pidiendo un café.
 */
export function mensajeParaLaPantalla(fallo, respaldo) {
    //  Los dos que SÍ pasan, y los dos por lo mismo: alguien escribió ese texto
    //  para una pantalla. El de la API viene de su campo `mensaje`; el otro lo
    //  declara quien lo lanza.
    if (fallo instanceof ErrorDeApi)
        return fallo.message;
    if (fallo instanceof ErrorParaLaPantalla)
        return fallo.message;
    //  Se registra ANTES de decidir qué se enseña, y siempre: si esto sólo
    //  ocurriera en la rama genérica, el día que algo raro llegue como `ErrorDeApi`
    //  no quedaría rastro de qué pasó.
    console.error('[fialrest] fallo no previsto en la pantalla:', fallo);
    if (fallo instanceof TypeError)
        return SIN_CONEXION;
    return `${respaldo} ${MAS_TARDE}`;
}
//# sourceMappingURL=errores.js.map